import { useEffect, useMemo, useState } from "react";

// NS-style overhead platform sign (the suspended blue panel you see hanging
// above each track at a Dutch station). Unlike the wall-mounted "Vertrekstaat"
// in <DepartureBoard/>, this panel only shows the *single next* train at the
// chosen platform: big destination, train type with the NS double-arrow logo,
// the via-stops, a stylised train carriage layout, and a "Volgende trein"
// teaser line. An analog clock sits on the left; the spoor (track) number on
// the right.
//
// Visual reference: classic NS overhead displays (blue panel, white card with
// blue text, separate clock + spoor cards). We approximate the layout closely
// without trying to clone any specific revision.

type StopOption = {
  id: string;
  name: string;
  code: string;
  lat: number;
  lon: number;
  routes: string[];
};

type RouteGeometry = {
  route_id: string;
  route_short_name: string;
  route_long_name: string;
  route_color: string;
  stops: { id: string; name: string; code: string; lat: number; lon: number }[];
};

type Arrival = {
  route_id: string;
  route_short_name: string;
  route_color?: string;
  trip_id: string;
  trip_headsign: string;
  vehicle_id?: string;
  scheduled_arrival?: number;
  predicted_arrival?: number;
  scheduled_departure?: number;
  predicted_departure?: number;
  predicted: boolean;
  status?: string;
  stops_away: number;
};

export type StopArrivals = {
  stop_id: string;
  stop_name: string;
  stop_code?: string;
  arrivals: Arrival[];
  generated: number;
};

const ARRIVAL_REFRESH_MS = 15_000;

export function PlatformSign({
  visible,
  historyTs = null,
}: {
  visible: boolean;
  historyTs?: number | null;
}) {
  const [stops, setStops] = useState<StopOption[]>([]);
  const [stopsErr, setStopsErr] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string>("");
  const [data, setData] = useState<StopArrivals | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const historical = historyTs !== null;

  // Tick once a second so the clock hands move and the "Volgende trein"
  // countdown stays accurate without a refetch. Frozen while viewing
  // history so the sign reflects the scrubbed-to instant.
  useEffect(() => {
    if (historical) {
      setNow(historyTs! * 1000);
      return;
    }
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [historical, historyTs]);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/routes/geometry")
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<RouteGeometry[]>;
      })
      .then((routes) => {
        if (cancelled) return;
        const byId = new Map<string, StopOption>();
        for (const r of routes) {
          for (const s of r.stops ?? []) {
            const existing = byId.get(s.id);
            if (existing) {
              if (!existing.routes.includes(r.route_short_name)) {
                existing.routes.push(r.route_short_name);
              }
              continue;
            }
            byId.set(s.id, {
              id: s.id,
              name: s.name,
              code: s.code,
              lat: s.lat,
              lon: s.lon,
              routes: [r.route_short_name],
            });
          }
        }
        const list = Array.from(byId.values()).sort((a, b) =>
          a.name.localeCompare(b.name),
        );
        setStops(list);
        if (list.length > 0 && !selectedId) {
          const preferred =
            list.find((s) => /westlake/i.test(s.name)) ??
            list.find((s) => /sea-tac|seatac|airport/i.test(s.name)) ??
            list[0];
          setSelectedId(preferred.id);
        }
      })
      .catch((err) => {
        if (!cancelled) setStopsErr(String(err));
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedId || !visible) return;
    let cancelled = false;
    let timer: number | null = null;
    let debounce: number | null = null;

    const url = historical
      ? `/api/realtime/arrivals/at?stop_id=${encodeURIComponent(selectedId)}&ts=${encodeURIComponent(String(historyTs))}`
      : `/api/realtime/arrivals?stop_id=${encodeURIComponent(selectedId)}`;

    const load = () => {
      fetch(url)
        .then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json() as Promise<StopArrivals>;
        })
        .then((d) => {
          if (cancelled) return;
          setData(d);
          setLoadErr(null);
        })
        .catch((err) => {
          if (!cancelled) setLoadErr(String(err));
        });
    };

    if (historical) {
      debounce = window.setTimeout(load, 120);
    } else {
      load();
      timer = window.setInterval(load, ARRIVAL_REFRESH_MS);
    }
    return () => {
      cancelled = true;
      if (timer !== null) window.clearInterval(timer);
      if (debounce !== null) window.clearTimeout(debounce);
    };
  }, [selectedId, visible, historical, historyTs]);

  const upcoming = useMemo(() => buildUpcoming(data, now), [data, now]);
  const next = upcoming[0] ?? null;
  const after = upcoming[1] ?? null;

  return (
    <div className="sign-wrap">
      <div className="sign-controls">
        <label className="sign-station-label" htmlFor="sign-station">
          Station
        </label>
        <select
          id="sign-station"
          className="sign-station-select"
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
        >
          {stops.length === 0 && <option value="">Loading stations…</option>}
          {stops.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
              {s.routes.length > 0 ? `  ·  ${s.routes.join(" / ")} Line` : ""}
            </option>
          ))}
        </select>
        {stopsErr && <span className="sign-error">{stopsErr}</span>}
        <span
          className={`sign-pill ${loadErr ? "err" : data ? "ok" : "wait"}`}
          aria-live="polite"
        >
          {loadErr
            ? "● offline"
            : data
              ? `● live · ${data.stop_name}`
              : "○ verbinden…"}
        </span>
      </div>

      <div className="sign-stage">
        <div className="sign-rig" aria-hidden />
        <div className="sign-housing" role="region" aria-label="Platform sign">
          <div className="sign-clock-card">
            <AnalogClock nowMs={now} />
          </div>

          <div className="sign-main-card">
            {next ? (
              <SignContent next={next} after={after} now={now} />
            ) : (
              <div className="sign-empty">
                {loadErr
                  ? "Geen verbinding met de server"
                  : data
                    ? "Geen vertrektijden bekend"
                    : "Vertrektijden laden…"}
              </div>
            )}
          </div>

          <div className="sign-spoor-card">
            <div className="sign-spoor-label">spoor</div>
            <div className="sign-spoor-number">
              {next ? next.platform : "—"}
              {next?.platformSuffix && (
                <span className="sign-spoor-suffix">{next.platformSuffix}</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SignContent({
  next,
  after,
  now,
}: {
  next: Upcoming;
  after: Upcoming | null;
  now: number;
}) {
  const cancelled = /cancel/i.test(next.status);
  return (
    <div className="sign-content">
      <div className="sign-headline">
        <span className="sign-time">{formatHM(next.scheduledMs)}</span>
        {next.delayMin > 0 && (
          <span className="sign-delay">+{next.delayMin}</span>
        )}
        <span className="sign-type">{next.trainType}</span>
        <NsLogo />
      </div>
      <div className="sign-destination">{next.destination}</div>
      {next.via && <div className="sign-via">via {next.via}</div>}

      <CarriageDiagram />

      <div className="sign-footer">
        {cancelled ? (
          <span className="sign-cancelled">Deze trein rijdt niet</span>
        ) : after ? (
          <>
            <span className="sign-volgende-label">Volgende trein:</span>{" "}
            <span className="sign-volgende-time">
              {formatHM(after.scheduledMs)}
            </span>{" "}
            <span className="sign-volgende-type">{after.trainType}</span>{" "}
            <span className="sign-volgende-dest">{after.destination}</span>
            {after.delayMin > 0 && (
              <span className="sign-volgende-delay"> +{after.delayMin}</span>
            )}
          </>
        ) : (
          <span className="sign-volgende-label">
            {minutesUntil(next.whenMs, now)}
          </span>
        )}
      </div>
    </div>
  );
}

export function minutesUntil(targetMs: number, nowMs: number): string {
  const mins = Math.max(0, Math.round((targetMs - nowMs) / 60_000));
  if (mins <= 0) return "Vertrekt nu";
  if (mins === 1) return "Over 1 minuut";
  return `Over ${mins} minuten`;
}

export type Upcoming = {
  key: string;
  whenMs: number;
  scheduledMs: number;
  destination: string;
  via: string;
  trainType: string;
  routeColor: string;
  delayMin: number;
  predicted: boolean;
  status: string;
  platform: string;
  platformSuffix: string;
};

export function buildUpcoming(data: StopArrivals | null, nowMs: number): Upcoming[] {
  if (!data) return [];
  const out: Upcoming[] = [];
  for (const a of data.arrivals) {
    const schedSec = a.scheduled_departure || a.scheduled_arrival || 0;
    const predSec = a.predicted_departure || a.predicted_arrival || 0;
    const bestSec = predSec || schedSec;
    if (!bestSec) continue;
    const bestMs = bestSec * 1000;
    if (bestMs < nowMs - 60_000) continue;
    const delayMin =
      predSec && schedSec ? Math.max(0, Math.round((predSec - schedSec) / 60)) : 0;
    const { destination, via } = splitHeadsign(a.trip_headsign);
    const { platform, platformSuffix } = platformFor(a.route_short_name, a.trip_id);
    out.push({
      key: `${a.trip_id}-${schedSec}`,
      whenMs: bestMs,
      scheduledMs: (schedSec || bestSec) * 1000,
      destination,
      via,
      trainType: classifyTrainType(a.route_short_name, a.stops_away),
      routeColor: a.route_color ? `#${a.route_color}` : "#ffffff",
      delayMin,
      predicted: a.predicted,
      status: a.status || "",
      platform,
      platformSuffix,
    });
  }
  out.sort((a, b) => a.whenMs - b.whenMs);
  return out;
}

export function splitHeadsign(headsign: string): { destination: string; via: string } {
  if (!headsign) return { destination: "—", via: "" };
  const m = headsign.match(/^(.*?)\s+via\s+(.+)$/i);
  if (m) return { destination: m[1].trim(), via: m[2].trim() };
  return { destination: headsign, via: "" };
}

// Sound Transit's light rail doesn't carry NS-style train classes (Intercity
// vs Sprinter), so we synthesise one from the line: the 1 Line behaves like
// an Intercity (long-haul, all major destinations), the 2 Line is more of a
// Sprinter feel. Anything else falls back to "Sneltrein". This keeps the NS
// look intact without claiming data we don't have.
export function classifyTrainType(routeShort: string, _stopsAway: number): string {
  const r = (routeShort || "").trim();
  if (/^1$|line\s*1|red/i.test(r)) return "Intercity";
  if (/^2$|line\s*2|blue/i.test(r)) return "Sprinter";
  if (/^t$|tacoma|streetcar/i.test(r)) return "Sprinter";
  return "Sneltrein";
}

// Same reasoning as DepartureBoard: there's no real platform/track number in
// the upstream data, so we deterministically derive a plausible "spoor" label
// from the line + trip id. The point is to mirror the NS layout, not to
// fabricate authoritative data.
export function platformFor(routeShort: string, tripId: string): {
  platform: string;
  platformSuffix: string;
} {
  const m = (routeShort || "").match(/(\d+)/);
  const base = m ? m[1] : "1";
  // Stable per-trip suffix: "a" or "b" (a Dutch platform half), or none.
  let h = 0;
  for (let i = 0; i < tripId.length; i++) h = (h * 31 + tripId.charCodeAt(i)) | 0;
  const pick = Math.abs(h) % 3;
  const suffix = pick === 0 ? "" : pick === 1 ? "a" : "b";
  return { platform: base, platformSuffix: suffix };
}

export function formatHM(ms: number): string {
  const d = new Date(ms);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

// The Dutch railways double-arrow mark, simplified as inline SVG so it scales
// crisply next to the train type label.
function NsLogo() {
  return (
    <svg
      className="sign-ns-logo"
      viewBox="0 0 60 30"
      width="60"
      height="30"
      aria-label="NS"
    >
      <rect x="0" y="0" width="60" height="30" rx="3" ry="3" fill="#003082" />
      {/* Two horizontal arrows pointing in opposite directions, the iconic NS
          mark. Drawn as solid white shapes. */}
      <path
        d="M6 11 L40 11 L40 6 L54 15 L40 24 L40 19 L6 19 Z"
        fill="#ffd900"
      />
      <path
        d="M54 19 L20 19 L20 24 L6 15 L20 6 L20 11 L54 11 Z"
        fill="#ffd900"
        opacity="0"
      />
    </svg>
  );
}

// Stylised train layout strip — NS displays show a row of carriage boxes
// with platform-section letters (A..M) underneath, indicating where each
// carriage will stop. We don't have real consist data, so we draw a fixed
// 5-carriage train with letters K..E (matching the photo's right-to-left
// reading order) purely as decoration.
function CarriageDiagram() {
  const sections = ["K", "J", "H", "G", "F", "E"];
  return (
    <div className="sign-carriages" aria-hidden>
      <div className="sign-carriage-arrow">←</div>
      <div className="sign-carriage-strip">
        <svg viewBox="0 0 220 22" preserveAspectRatio="none" width="100%" height="22">
          {/* Locomotive nose on the left */}
          <polygon points="0,11 10,2 10,20" fill="#003082" />
          <rect x="10" y="2" width="200" height="18" rx="2" fill="#003082" />
          {/* Carriage divider lines */}
          {[50, 90, 130, 170].map((x) => (
            <line key={x} x1={x} y1="2" x2={x} y2="20" stroke="#fff" strokeWidth="0.8" />
          ))}
          {/* Window dots per carriage */}
          {[20, 30, 40, 60, 70, 80, 100, 110, 120, 140, 150, 160, 180, 190, 200].map(
            (x) => (
              <rect key={x} x={x} y="7" width="6" height="3" fill="#ffffff" opacity="0.9" />
            ),
          )}
          {/* Tail nose on the right */}
          <polygon points="210,2 220,11 210,20" fill="#003082" />
        </svg>
      </div>
      <div className="sign-carriage-sections">
        {sections.map((s) => (
          <span key={s} className="sign-carriage-section">
            {s}
          </span>
        ))}
      </div>
    </div>
  );
}

function AnalogClock({ nowMs }: { nowMs: number }) {
  const d = new Date(nowMs);
  const sec = d.getSeconds() + d.getMilliseconds() / 1000;
  const min = d.getMinutes() + sec / 60;
  const hr = (d.getHours() % 12) + min / 60;

  const hourAngle = hr * 30; // 360 / 12
  const minuteAngle = min * 6; // 360 / 60
  const secondAngle = sec * 6;

  // 12 hour ticks: longer at 3/6/9/12, shorter elsewhere.
  const ticks = [];
  for (let i = 0; i < 12; i++) {
    const angle = (i * 30 * Math.PI) / 180;
    const r1 = 44;
    const r2 = i % 3 === 0 ? 36 : 39;
    ticks.push(
      <line
        key={i}
        x1={50 + Math.sin(angle) * r1}
        y1={50 - Math.cos(angle) * r1}
        x2={50 + Math.sin(angle) * r2}
        y2={50 - Math.cos(angle) * r2}
        stroke="#1f4ea5"
        strokeWidth={i % 3 === 0 ? 3 : 2}
        strokeLinecap="round"
      />,
    );
  }

  return (
    <svg className="sign-clock" viewBox="0 0 100 100" aria-label="Clock">
      <circle cx="50" cy="50" r="48" fill="#ffffff" stroke="#dde2ea" strokeWidth="1" />
      {ticks}
      {/* Hour hand */}
      <line
        x1="50"
        y1="50"
        x2={50 + Math.sin((hourAngle * Math.PI) / 180) * 24}
        y2={50 - Math.cos((hourAngle * Math.PI) / 180) * 24}
        stroke="#1f4ea5"
        strokeWidth="5"
        strokeLinecap="round"
      />
      {/* Minute hand */}
      <line
        x1="50"
        y1="50"
        x2={50 + Math.sin((minuteAngle * Math.PI) / 180) * 36}
        y2={50 - Math.cos((minuteAngle * Math.PI) / 180) * 36}
        stroke="#1f4ea5"
        strokeWidth="3"
        strokeLinecap="round"
      />
      {/* Second hand (red, with the classic NS counterweighted ring) */}
      <line
        x1="50"
        y1="50"
        x2={50 + Math.sin((secondAngle * Math.PI) / 180) * 38}
        y2={50 - Math.cos((secondAngle * Math.PI) / 180) * 38}
        stroke="#d4283c"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <circle
        cx={50 + Math.sin((secondAngle * Math.PI) / 180) * 34}
        cy={50 - Math.cos((secondAngle * Math.PI) / 180) * 34}
        r="3.5"
        fill="none"
        stroke="#d4283c"
        strokeWidth="1.5"
      />
      <circle cx="50" cy="50" r="2.5" fill="#1f4ea5" />
    </svg>
  );
}
